<template>
  <div class="guide-screen">
    <div class="container">
      <div class="guide-screen__card">
        <h4 class="guide-screen__title">{{ textTitle }}</h4>
        <h6 class="guide-screen__subtitle">{{ textSubtitle }}</h6>

        <ul class="guide-screen__list-group">
          <li class="guide-screen__list-item">
            <div class="d-flex align-items-center flex-nowrap">
              <Icon
                :name="
                  currentUser.address.id ? 'check-circle-icon' : 'circle-icon'
                "
                :width="42"
                :height="42"
                style="min-width: 42px;"
              />
              <span
                class="guide-screen__text"
                :class="[
                  currentUser.address.id ? 'guide-screen__text--done' : ''
                ]"
              >
                Informar endereço
              </span>
              <Icon
                id="address-tooltip"
                :name="'warning-icon'"
                :width="20"
                :height="20"
                style="outline: none; min-width: 20px;"
              />
            </div>

            <div v-if="currentUser.address.id">
              <span class="guide-screen__done ml-5 mr-4">Finalizado</span>
            </div>

            <button
              v-else
              class="button4 ml-4"
              id="address-step-btn"
              @click="updateDoctorAddress"
            >
              Prosseguir
            </button>
          </li>

          <li class="guide-screen__list-item">
            <div class="guide-screen__group">
              <Icon
                :name="hasSlots ? 'check-circle-icon' : 'circle-icon'"
                :width="42"
                :height="42"
                style="min-width: 42px;"
              />
              <span
                class="guide-screen__text"
                :class="[hasSlots ? 'guide-screen__text--done' : '']"
              >
                Confirgurar agenda de atendimento
              </span>
              <Icon
                id="schedule-tooltip"
                :name="'warning-icon'"
                :width="20"
                :height="20"
                style="outline: none; min-width: 20px;"
              />
            </div>

            <div v-if="hasSlots">
              <span class="guide-screen__done ml-5 mr-4">Finalizado</span>
            </div>

            <button
              v-else-if="!hasSlots && currentUser.address.id"
              class="button4 ml-4"
              id="schedule-step-btn"
              @click="pushTo('ScheduleSettings')"
            >
              Prosseguir
            </button>
          </li>

          <li class="guide-screen__list-item">
            <div class="guide-screen__group">
              <Icon
                :name="
                  currentUser.account.id ? 'check-circle-icon' : 'circle-icon'
                "
                :width="42"
                :height="42"
                style="min-width: 42px;"
              />
              <span
                class="guide-screen__text"
                :class="[
                  currentUser.account.id ? 'guide-screen__text--done' : ''
                ]"
              >
                Informe o valor da sua consulta
              </span>
              <Icon
                id="account-tooltip"
                :name="'warning-icon'"
                :width="20"
                :height="20"
                style="outline: none; min-width: 20px;"
              />
            </div>

            <div v-if="currentUser.account.id">
              <span class="guide-screen__done ml-5 mr-4">Finalizado</span>
            </div>

            <button
              v-else-if="!currentUser.account.id && hasSlots"
              class="button4 ml-4"
              id="account-step-btn"
              @click="activeDoctorAccount"
            >
              Prosseguir
            </button>
          </li>

          <li class="guide-screen__list-item">
            <div class="guide-screen__group">
              <Icon
                :name="
                  hasBankAccount ? 'check-circle-icon' : 'circle-icon'
                "
                :width="42"
                :height="42"
                style="min-width: 42px;"
              />
              <span
                class="guide-screen__text"
                :class="[
                  hasBankAccount ? 'guide-screen__text--done' : ''
                ]"
              >
                Adicionar conta bancária
              </span>
              <Icon
                id="bankAccount-tooltip"
                :name="'warning-icon'"
                :width="20"
                :height="20"
                style="outline: none; min-width: 20px;"
              />
            </div>

            <div v-if="hasBankAccount">
              <span class="guide-screen__done ml-5 mr-4">Finalizado</span>
            </div>

            <template v-else-if="!hasBankAccount && currentUser.account.id">
              <div>
                <button
                  class="button4 ml-4"
                  id="account-step-btn"
                  @click="createBankAccount"
                >
                  Prosseguir
                </button>
                <button
                  v-if="hasActiveRelationshipWithInstitutions"
                  class="button4 ml-4"
                  @click="skipCompleteRegistration"
                >
                  Pular
                </button>
              </div>
            </template>
          </li>
        </ul>

        <div
          v-if="currentUser.address.id && currentUser.account.id && hasBankAccount"
          class="d-flex align-items-center justify-content-center mt-4"
        >
          <Button
            id="completeGuideBtn"
            variant="primary"
            size="lg"
            @click.native="completeUserGuide"
          >
            Ir para painel administrativo
          </Button>
        </div>

        <b-modal
          id="updateAddress"
          ref="updateAddress"
          size="lg"
          centered
          hide-footer
          hide-header
        >
          <div class="profile" :style="useStyles.profile">
            <Address
              @addressCreated="addressCreated"
              :style="useStyles.profileCard"
            />
          </div>
        </b-modal>

        <div>
            <b-modal
              id="bankAccount"
              ref="bankAccount"
              size="lg"
              centered
              hide-footer
              hide-header
            >
              <BankAccountForm @bankAccountCreated="bankAccountCreated" />
            </b-modal>
          </div>

        <b-modal
          id="activeAccount"
          ref="activeAccount"
          size="md"
          centered
          hide-footer
          hide-header
        >
          <div class="profile" :style="useStyles.profile">
            <Account
              @accountActivated="accountActivated"
              :style="[useStyles.profileCard, { textAlign: 'center' }]"
            />
          </div>
        </b-modal>

        <b-tooltip target="address-tooltip" placement="right">
          Informe o endereço para prosseguir com o seu perfil.
        </b-tooltip>

        <b-tooltip target="schedule-tooltip" placement="right">
          Configure a sua agenda para poder realizar suas consultas de
          videochamada.
        </b-tooltip>

        <b-tooltip target="bankAccount-tooltip" placement="right">
          Essa conta bancária deverá ter vocẽ como titular. É para ela que você irá transferir os valores pagos por seus pacientes
        </b-tooltip>

        <b-tooltip target="account-tooltip" placement="right">
          Você poderá alterar o valor das suas consultas sempre que quiser acessando as configurações do seu perfil.
        </b-tooltip>
      </div>
    </div>
  </div>
</template>

<script>
import { doctorMethods } from "../state/helpers";
import Icon from "../components/CustomIcons";
import Address from "../components/Address";
import Account from "../components/WireCardAccount";
import BankAccountForm from '../components/BankAccountForm';
import Button from '../components/Button';
export default {
  name: "GuidePage",
  components: {
    Icon,
    Address,
    Account,
    BankAccountForm,
    Button
  },
  data() {
    return {
      hasSlots: false,
      hasBankAccount: false,
    };
  },
  mounted() {
    this.fetchDoctorsAvailables({ id: this.currentUser.id }).then(response => {
      if (response.data.length) this.hasSlots = true;
    });
    this.$store.commit("auth/SET_USER_GUIDE", true);
    if (this.currentUser.bank_account) {
      this.hasBankAccount = true;
    }
  },
  methods: {
    ...doctorMethods,
    pushTo(routeName) {
      this.$router.push({ name: routeName });
    },
    completeUserGuide() {
      this.pushTo("Dashboard");
    },
    skipCompleteRegistration() {
      this.$store.commit("auth/SET_USER_GUIDE", false);
      this.completeUserGuide();
    },
    updateDoctorAddress() {
      return this.$bvModal.show("updateAddress");
    },
    addressCreated() {
      return this.$bvModal.hide("updateAddress");
    },
    activeDoctorAccount() {
      return this.$bvModal.show("activeAccount");
    },
    createBankAccount() {
      return this.$bvModal.show("bankAccount");
    },
    bankAccountCreated() {
      this.$store.dispatch("user/showUser", this.currentUser.id).then(response => {
        if (response.data.bank_account) this.hasBankAccount = true;
      });
      this.$store.commit("auth/SET_USER_GUIDE", false);
      return this.$bvModal.hide("bankAccount");
    },
    accountActivated() {
      return this.$bvModal.hide("activeAccount");
    }
  },
  computed: {
    currentUser() {
      return this.$store.state.user.currentUser;
    },
    hasActiveRelationshipWithInstitutions() {
      return this.$store.getters["auth/hasActiveRelationshipWithInstitutions"];
    },
    textTitle() {
      return this.currentUser.address.id && this.currentUser.account.id && this.hasBankAccount
        ? "Complete seu cadastro!"
        : `Bem-vindo(a), ${this.currentUser.first_name}`;
    },
    textSubtitle() {
      return this.currentUser.address.id && this.currentUser.account.id && this.hasBankAccount
        ? "Falta pouco para começar a utilizar o Videomedi. Abaixo algumas informações que precisamos para completar seu perfil"
        : "Antes de começar a usar e aproveitar todos os benefícios do Videomedi, precisamos de alguns dados. É bem rápido, vamos lá?";
    },
    useStyles() {
      return {
        profile: {
          minHeight: "0",
          margin: "0",
          background: "#fff",
          border: "none"
        },
        profileCard: {
          margin: "0",
          boxShadow: "none",
          border: "none"
        }
      };
    }
  }
};
</script>
